import client from '../components/ApolloClient';
import gql from 'graphql-tag';

// Categories (Inquiry/Appeal/Grievance) -> Subcategories (determination type / request
// type) -> Items (denial reason codes). slaConfigs and evidenceConfigs now live directly
// on the category (not per-subcategory/per-item) -- one SLA/evidence list per category,
// shared across all of its subcategories. userType is optional on the BE -- omitting it
// returns every category regardless of MEMBER/PROVIDER, passing it filters server-side.
// `id` on subcategories isn't part of the BE-documented response shape but is requested
// here anyway -- it's the only way to get the UUID createMemberSupportCase/
// updateMemberSupportCase need for `subcategoryId`.
export const GET_SUPPORT_CATEGORIES = gql`
  query GetSupportCategories($userType: String) {
    getSupportCategories(userType: $userType) {
      id
      code
      label
      slaConfigs {
        id
        lobId
        slaValue
        slaUnit
      }
      evidenceConfigs {
        evidenceTypeCode
        label
        isAutoSelectable
        isRequired
      }
      subcategories {
        id
        code
        label
        userTypes
        items {
          code
          label
        }
      }
    }
  }
`;

// Shared field set for createMemberSupportCase/updateMemberSupportCase only -- both return
// SupportCaseResponse, same as getMemberSupportCase, but kept deliberately smaller than that
// query's own field set (GET_MEMBER_SUPPORT_CASE below) rather than reused for it: backend
// confirmed 2026-10-01 that getMemberSupportCase's extended fields (pendingAction/
// infoRequests/classification/slaPause/documents.downloadUrl) are deployed for that query
// specifically, not confirmed for these two mutations -- no reason to request them here
// until/unless that's confirmed too.
const SUPPORT_CASE_RESPONSE_FRAGMENT = gql`
  fragment SupportCaseResponseFields on SupportCaseResponse {
    caseInfo {
      id
      caseId
      category
      status
      categoryId
      categoryName
      subcategoryId
      subcategoryName
      subcategoryItemId
      subcategoryItemName
      subcategoryLabel
    }
    requester {
      patientId
      patientFullName
      requesterType
      requesterRelationshipToMember
      intakeChannel
    }
    subject {
      description
      caseSourceType
      caseSourceId
      denialReasonCode
      denialReason
      partyType
      partyName
      partyId
      incidentDate
    }
    documents {
      id
      documentType
      fileName
      storageUri
      description
      isActive
    }
  }
`;

export const CREATE_MEMBER_SUPPORT_CASE = gql`
  mutation CreateMemberSupportCase($input: SupportCaseInput!) {
    createMemberSupportCase(input: $input) {
      ...SupportCaseResponseFields
    }
  }
  ${SUPPORT_CASE_RESPONSE_FRAGMENT}
`;

export const UPDATE_MEMBER_SUPPORT_CASE = gql`
  mutation UpdateMemberSupportCase($id: ID!, $input: SupportCaseInput!) {
    updateMemberSupportCase(id: $id, input: $input) {
      ...SupportCaseResponseFields
    }
  }
  ${SUPPORT_CASE_RESPONSE_FRAGMENT}
`;

// Full shape confirmed ACTUALLY deployed 2026-10-01 -- verified against a real captured
// network request/response (not just the broader schema the backend team had separately
// described -- that draft included extra fields like caseInfo.originalCaseId/isReopened,
// requester.requesterId/requesterName/requesterContact/requesterNpi, subject.liveNotes/
// desiredOutcome, and documents.commentId/uploadedByType/uploadedAt/downloadUrl, none of
// which this confirmed-working query actually requests -- so they're left out here too,
// since querying a field the deployed schema doesn't resolve would fail the whole request,
// not just return null for that field). Adds classification, pendingAction, infoRequests
// (incl. each request's own response, when RESPONDED), and slaPause on top of the base
// caseInfo/requester/subject/documents every other case query has. Deliberately not sharing
// SUPPORT_CASE_RESPONSE_FRAGMENT (see its own comment) -- this query's extended fields
// aren't confirmed for createMemberSupportCase/updateMemberSupportCase. infoRequests/
// pendingAction used to be mocked client-side (AdditionalInfoRequest, see
// SUPPORT_REQUIREMENTS.md §11a.4) -- now read straight off this response.
export const GET_MEMBER_SUPPORT_CASE = gql`
  query GetMemberSupportCase($id: ID!) {
    getMemberSupportCase(id: $id) {
      caseInfo {
        id
        caseId
        category
        status
        categoryId
        categoryName
        subcategoryId
        subcategoryName
        subcategoryItemId
        subcategoryItemName
        subcategoryLabel
      }
      requester {
        patientId
        patientFullName
        requesterType
        requesterRelationshipToMember
        intakeChannel
      }
      subject {
        description
        caseSourceType
        caseSourceId
        denialReasonCode
        denialReason
        partyType
        partyName
        partyId
        incidentDate
      }
      classification {
        clinicalClassification
        priorityClassification
        expeditedJustification
      }
      documents {
        id
        documentType
        fileName
        storageUri
        description
        isActive
      }
      pendingAction {
        isActionPending
        openInfoRequestCount
        actionPendingFrom
        actionPendingSince
        actionDueAt
      }
      infoRequests {
        id
        status
        requestedFromType
        message
        requestedDocumentTypes
        dueAt
        overdue
        requestedAt
        requestedByName
        cancelledAt
        expiredAt
        response {
          commentId
          body
          authorName
          authorType
          respondedAt
          documents {
            id
            documentType
            fileName
            storageUri
            description
            isActive
          }
        }
      }
      slaPause {
        slaStatus
        slaPausedAt
        slaTotalPausedMinutes
        pauses
      }
    }
  }
`;

// Support history list -- separate, flatter response shape (SupportCaseListPage: paginated
// content/pagination envelope, and just the handful of summary fields the list row needs),
// so it gets its own fragment-free query rather than reusing SUPPORT_CASE_RESPONSE_FRAGMENT.
// status/category are real server-side filters here (unlike the retired getSupportCases,
// patientId is required and singular, not an array).
// isActionPending/openInfoRequestCount/slaStatus/slaPausedAt confirmed deployed 2026-10-01.
// actionPendingFrom (confirmed deployed 2026-10-02) is a real server-side filter too --
// backend confirmed passing 'MEMBER' here is how "Needs your action" (FR-49) is meant to be
// fetched, mirroring the partner-side REST API's own `GET .../support-cases?
// actionPendingFrom=MEMBER` (org-wide) and `GET .../support-cases/patient/:patientId?
// actionPending=true` (patient-scoped "Action required tab") precedents -- ReusableCategoriesContainer
// passes it only while that tab is active, undefined otherwise (omitting a GraphQL string
// variable sends no filter, same as the already-established `status` pattern below).
export const GET_MEMBER_SUPPORT_CASES = gql`
  query GetMemberSupportCases(
    $patientId: ID!
    $status: String
    $category: String
    $actionPendingFrom: String
    $page: Int
    $size: Int
  ) {
    getMemberSupportCases(
      patientId: $patientId
      status: $status
      category: $category
      actionPendingFrom: $actionPendingFrom
      page: $page
      size: $size
    ) {
      content {
        id
        caseId
        category
        status
        assignedToName
        slaDueAt
        isActionPending
        openInfoRequestCount
        slaStatus
        slaPausedAt
        receivedAt
      }
      pagination {
        page
        size
        totalElements
        totalPages
        hasNext
        hasPrevious
      }
    }
  }
`;

// Confirmed deployed 2026-10-01 (verified via a real captured request/response) -- the
// member's response to one of their own OPEN infoRequests (AdditionalInfoRequest, §11a).
// Replaces the earlier REST-only reference (requestAdditionalInfoOnCase's mirror,
// mockRespondToInfoRequest) -- that mock is retired now that this mutation is real.
// `input.documents[]` only takes documentType/fileName/storageUri/description (no id/
// isActive/commentId/uploadedByType/uploadedAt -- those come back on the response instead,
// server-assigned). `casePendingAction` (not `pendingAction`, unlike getMemberSupportCase)
// is this mutation's own name for the same refreshed isActionPending/openInfoRequestCount/
// actionPendingFrom/actionPendingSince/actionDueAt block.
export const SUBMIT_MEMBER_SUPPORT_CASE_INFO = gql`
  mutation SubmitMemberSupportCaseInfo($caseId: ID!, $input: SupportCaseCommentInput!) {
    submitMemberSupportCaseInfo(caseId: $caseId, input: $input) {
      id
      caseId
      commentType
      visibility
      body
      infoRequestId
      author {
        id
        name
        type
      }
      createdAt
      documents {
        id
        documentType
        fileName
        storageUri
        description
        isActive
        commentId
        uploadedByType
        uploadedAt
      }
      casePendingAction {
        isActionPending
        openInfoRequestCount
        actionPendingFrom
        actionPendingSince
        actionDueAt
      }
    }
  }
`;

export default {
  getSupportCategories(userType, query = GET_SUPPORT_CATEGORIES) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query,
          variables: { userType },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.getSupportCategories);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  // x-user is only required by createMemberSupportCase (see UI Integration Guide);
  // Authorization is already attached to every request by ApolloClient's request link.
  createMemberSupportCase(input, xUser, mutation = CREATE_MEMBER_SUPPORT_CASE) {
    return new Promise((resolve, reject) => {
      client
        .mutate({
          mutation,
          variables: { input },
          context: xUser ? { headers: { 'x-user': xUser } } : undefined,
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.createMemberSupportCase);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updateMemberSupportCase(id, input, mutation = UPDATE_MEMBER_SUPPORT_CASE) {
    return new Promise((resolve, reject) => {
      client
        .mutate({
          mutation,
          variables: { id, input },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.updateMemberSupportCase);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getMemberSupportCase(id, query = GET_MEMBER_SUPPORT_CASE) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query,
          variables: { id },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.getMemberSupportCase);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getMemberSupportCases(variables, query = GET_MEMBER_SUPPORT_CASES) {
    return new Promise((resolve, reject) => {
      client
        .query({
          query,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.getMemberSupportCases);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  submitMemberSupportCaseInfo(caseId, input, mutation = SUBMIT_MEMBER_SUPPORT_CASE_INFO) {
    return new Promise((resolve, reject) => {
      client
        .mutate({
          mutation,
          variables: { caseId, input },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.submitMemberSupportCaseInfo);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};
